import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { KeywordFilteringGq } from '../../../../../../../graphql/projects/models/KeywordFilteringGq';
import { KeywordPagingGq } from '../../../../../../../graphql/projects/models/KeywordPagingGq';
import { KeywordSelecting, KeywordSelectingGq } from '../../../../../../../graphql/projects/models/KeywordSelectingGq';

const deepEqual = require('deep-equal');

export interface ConflictTableState {
  filterState: KeywordFilteringGq;
  setFilterState: Dispatch<SetStateAction<KeywordFilteringGq>>;
  backendFilterState: KeywordFilteringGq;
  backendSelectionState: KeywordSelectingGq;
  resetFilterState: () => void;
  hasFiltering: boolean;
  selectionState: KeywordSelecting;
  setSelectionState: Dispatch<SetStateAction<KeywordSelecting>>;
  handleSelectToggle: (id: number) => void;
  handleSelectMany: (ids: number[], selecting: boolean) => void;
  handleResetSelection: () => void;
}

const emptyFilter: KeywordFilteringGq = {
  projectId: 0,
  keyword: {
    searchTerm: '',
  },
  conflictTagFilters: [],
};

export function useConflictTableState(
  initialFilterState?: Partial<KeywordFilteringGq> & { projectId: number },
  initialSelectionState?: KeywordSelecting,
  initialPaginationState?: Partial<KeywordPagingGq>
): ConflictTableState {
  // Filtering
  const [filterState, setFilterState] = useState<KeywordFilteringGq>({
    ...emptyFilter,
    ...initialFilterState,
  });

  // Logic for resetting initial filter state
  // This should only happen in case it's new
  const [previousStartFilterState, setPreviousStartFilterState] = useState(initialFilterState);
  useEffect(() => {
    // Check if it's actually new at a granular level, and not only reference equality
    // deepEqual helps with that
    if (deepEqual(previousStartFilterState, initialFilterState)) {
      return;
    }

    // If new - reset the filters
    setPreviousStartFilterState(initialFilterState);
    setFilterState((prev) => ({ ...prev, ...initialFilterState }));
  }, [initialFilterState]);

  const [hasFiltering, setHasFiltering] = useState(false);

  useEffect(() => {
    if (
      !deepEqual(
        {
          ...emptyFilter,
          ...previousStartFilterState,
        },
        { ...filterState, archived: false, showUntagged: false }
      )
    ) {
      setHasFiltering(true);
    } else setHasFiltering(false);
  }, [filterState, previousStartFilterState]);

  const resetFilterState = useCallback(
    () =>
      setFilterState({
        ...emptyFilter,
        ...previousStartFilterState,
      }),
    [previousStartFilterState]
  );

  const backendFilterState = filterState;

  // Selection
  const [selectionState, setSelectionState] = useState<KeywordSelecting>(
    initialSelectionState ?? {
      invertSelection: false,
      keywordIds: {},
      emptyIsAll: true,
    }
  );

  const handleSelectToggle = useCallback(
    (id: number) =>
      setSelectionState((prevSelectionState) => {
        let keywordIds = { ...prevSelectionState.keywordIds };

        const keywordIsSelected = !!keywordIds[id];
        if (!keywordIsSelected) {
          keywordIds[id] = true;
        } else if (!keywordIsSelected) {
          delete keywordIds[id];
        }

        return { ...prevSelectionState, keywordIds: keywordIds };
      }),
    []
  );

  const handleSelectMany = useCallback(
    (ids: number[], selecting: boolean) =>
      setSelectionState((prevSelectionState) => {
        let keywordIds = { ...prevSelectionState.keywordIds };
        let dataChanged = false;
        ids.forEach((id) => {
          const keywordIsSelected = !!keywordIds[id];
          if (selecting && !keywordIsSelected) {
            // Add selection
            keywordIds[id] = true;
            dataChanged = true;
          } else if (!selecting && keywordIsSelected) {
            // Remove selection
            delete keywordIds[id];
            dataChanged = true;
          }
        });

        if (!dataChanged) {
          return prevSelectionState;
        }

        return { ...prevSelectionState, keywordIds: keywordIds };
      }),
    []
  );

  const handleResetSelection = useCallback(
    () =>
      setSelectionState(
        initialSelectionState ?? {
          invertSelection: false,
          keywordIds: [],
          emptyIsAll: true,
        }
      ),
    []
  );

  const backendSelectionState = useMemo(
    (): KeywordSelectingGq => ({
      invertSelection: selectionState.invertSelection,
      emptyIsAll: selectionState.emptyIsAll,
      keywordIds: Object.keys(selectionState.keywordIds).map((k) => +k),
    }),
    [selectionState]
  );

  return useMemo(
    () => ({
      hasFiltering,
      resetFilterState,
      filterState,
      setFilterState,
      backendFilterState,
      selectionState,
      setSelectionState,
      handleResetSelection,
      handleSelectToggle,
      handleSelectMany,
      backendSelectionState,
    }),
    [filterState, backendFilterState, selectionState]
  );
}
